import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import Markdown from "react-markdown";

import { ZOOM_SUMMARY_STATUS } from "~/providers/constants/zoomSummaryStatus";
import { ZoomSummaryStatus } from "~/providers/utils/types/zoomMeetingTypes";

interface SessionSummaryViewerProps {
  summaryStatus?: ZoomSummaryStatus | null;
  presignedUrl?: string;
  isLoadingSummary?: boolean;
}

const summaryStatusMessages: Record<ZoomSummaryStatus, string> = {
  IN_PROGRESS: "Summary is being processed. This may take a few minutes.",
  COMPLETED: "",
  FAILED: "No summary is available for this session.",
};

export const SessionSummaryViewer = ({
  summaryStatus,
  presignedUrl,
  isLoadingSummary = false,
}: SessionSummaryViewerProps) => {
  const [summaryContent, setSummaryContent] = useState("");
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isFetchingSummary, setIsFetchingSummary] = useState(false);

  useEffect(() => {
    if (!presignedUrl) {
      setSummaryContent("");
      setFetchError(null);
      return;
    }

    let isCancelled = false;

    const loadSummary = async () => {
      setIsFetchingSummary(true);
      setFetchError(null);

      try {
        const response = await fetch(presignedUrl);
        if (!response.ok) {
          throw new Error("Failed to load summary.");
        }

        const content = await response.text();
        if (!isCancelled) {
          setSummaryContent(content);
        }
      } catch {
        if (!isCancelled) {
          setFetchError("Unable to load summary content.");
          setSummaryContent("");
        }
      } finally {
        if (!isCancelled) {
          setIsFetchingSummary(false);
        }
      }
    };

    void loadSummary();

    return () => {
      isCancelled = true;
    };
  }, [presignedUrl]);

  if (
    isLoadingSummary ||
    isFetchingSummary ||
    summaryStatus === ZOOM_SUMMARY_STATUS.in_progress
  ) {
    return <Skeleton count={3} />;
  }

  if (
    !summaryStatus ||
    summaryStatus === ZOOM_SUMMARY_STATUS.failed ||
    fetchError
  ) {
    return (
      <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-600">
        {fetchError ?? summaryStatusMessages.FAILED}
      </div>
    );
  }

  if (!summaryContent.trim()) {
    return (
      <div className="py-2 text-gray-500">{summaryStatusMessages.FAILED}</div>
    );
  }

  return (
    <div>
      <div className="prose prose-sm max-w-none">
        <Markdown
          components={{
            h1: ({ children }) => (
              <h1 className="mb-3 mt-0 text-2xl font-medium">{children}</h1>
            ),
            h2: ({ children }) => (
              <h2 className="mb-2 mt-0 text-xl font-medium">{children}</h2>
            ),
            h3: ({ children }) => (
              <h3 className="mb-1 mt-0 text-base font-medium">{children}</h3>
            ),
            h4: ({ children }) => (
              <h4 className="mb-1 mt-0 text-base font-medium">{children}</h4>
            ),
            h5: ({ children }) => (
              <h5 className="mb-1 mt-0 text-base font-medium">{children}</h5>
            ),
          }}
        >
          {summaryContent}
        </Markdown>
      </div>
    </div>
  );
};
